import React from 'react'
import { motion } from 'framer-motion'
import { Code, Award, Heart } from 'lucide-react'
import { Profile } from '../types'
import { fadeUp, staggerContainer, viewportOnce } from '../lib/motion'

interface AboutProps {
  data: Profile
}

const About: React.FC<AboutProps> = ({ data }) => {
  const highlights = [
    {
      icon: <Code className="h-6 w-6" />,
      title: "Production Reliability",
      description: "Keeping banking loan systems up — monitoring, alerting, tracing, RCA, and leading war rooms"
    },
    {
      icon: <Code className="h-6 w-6" />,
      title: "Backend & Architecture",
      description: "Go and microservices background, now designing solutions as a Solution Architect at Vobis.io"
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: "Academic Excellence",
      description: "Graduated with GPA 3.78/4.00 in just 3.5 years from Brawijaya University"
    },
    {
      icon: <Heart className="h-6 w-6" />,
      title: "Passion for Technology",
      description: "Enthusiastic about cloud technologies, DevOps, and scalable system design"
    }
  ]

  return (
    <section id="about" className="py-20 bg-white dark:bg-gray-900">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            About Me
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
        </motion.div>

        <motion.div
          className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          {/* Left Column - Image and Stats */}
          <motion.div className="space-y-6 sm:space-y-8 order-2 lg:order-1" variants={fadeUp}>
            {/* Profile Image */}
            <div className="relative">
              <div className="w-64 h-64 sm:w-72 sm:h-72 lg:w-80 lg:h-80 mx-auto rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 p-1">
                <div className="w-full h-full rounded-2xl bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                  <img
                    src="https://r2.adityaariizkyy.com/Formal%20Abu2.jpg"
                    alt="Profile"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
              </div>
              {/* Floating Elements */}
              <motion.div
                className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700"
                initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={viewportOnce}
                transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.3 }}
              >
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-primary-600 dark:text-primary-400">3.78</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">GPA</div>
                </div>
              </motion.div>
              <motion.div
                className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 sm:p-4 border border-gray-200 dark:border-gray-700"
                initial={{ opacity: 0, scale: 0.6, rotate: 8 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={viewportOnce}
                transition={{ type: 'spring', stiffness: 200, damping: 14, delay: 0.45 }}
              >
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-primary-600 dark:text-primary-400">2+</div>
                  <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Years Exp</div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Column - Content */}
          <motion.div className="space-y-4 sm:space-y-6 order-1 lg:order-2" variants={fadeUp}>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                From backend engineering to banking production — keeping critical systems running.
              </h3>
              <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4 sm:mb-6">
                {data.summary}
              </p>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                Day to day I run production on Windows Server, RHEL, CentOS, and OpenShift, with Oracle and
                MS SQL behind it, and watch it through the ELK Stack, Grafana, and OpenTelemetry. My backend
                roots — Go, Kafka, gRPC, Redis — help me read failures from the code up, not just the dashboard.
              </p>
            </div>

            {/* Highlights */}
            <div className="space-y-3 sm:space-y-4">
              {highlights.map((highlight, index) => (
                <motion.div
                  key={index}
                  className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  whileHover={{ x: 4 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <div className="flex-shrink-0 p-1.5 sm:p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                    <div className="text-primary-600 dark:text-primary-400">
                      {highlight.icon}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm sm:text-base">
                      {highlight.title}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm">
                      {highlight.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Contact Info */}
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email</div>
                  <a href={`mailto:${data.email}`} className="text-primary-600 dark:text-primary-400 hover:underline">
                    {data.email}
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  )
}

export default About
